import { useTranslation } from 'react-i18next';

import AlertFill from '@/assets/icons/alert_fill.svg?component';
import CheckFill from '@/assets/icons/check_fill.svg?component';
import Loading from '@/assets/icons/loading_line.svg?component';
import QuestionFill from '@/assets/icons/question_fill.svg?component';
import { Badge, BadgeGroup, type BadgeTone } from '@/components/atoms/Badge';
import { SvgIcon, type SvgIconProps } from '@/components/atoms/SvgIcon/SvgIcon';
import hasOwnKey from '@/utils/hasOwnKey';

export type BadgeStatus =
    | 'created'
    | 'started'
    | 'error'
    | 'stopped'
    | 'finished'
    | 'stalled'
    | 'warning'
    | 'unknown';

interface StatusBadgeProps {
    /** A batch job's status as the backend sends it; one the portal does not know reads as unknown. */
    status: string;
    className?: string;
    isDraft?: boolean;
    draftLabel?: string;
}

const STATUS_ICON: Record<BadgeStatus, SvgIconProps['icon']> = {
    stalled: AlertFill,
    warning: AlertFill,
    finished: CheckFill,
    error: AlertFill,
    stopped: AlertFill,
    created: Loading,
    started: Loading,
    unknown: QuestionFill,
};

// An error is red; a warning or a stalled job is orange. The mapping carried over from the
// old styles had the two the other way round.
const TONE: Record<BadgeStatus, BadgeTone> = {
    stalled: 'warning',
    warning: 'warning',
    finished: 'success',
    error: 'danger',
    stopped: 'neutral',
    created: 'neutral',
    started: 'neutral',
    unknown: 'neutral',
};

function isBadgeStatus(status: string): status is BadgeStatus {
    return hasOwnKey(TONE, status);
}

// The backend adds job statuses without a portal release, so a new one must not break the list.
function toBadgeStatus(status: string): BadgeStatus {
    return isBadgeStatus(status) ? status : 'unknown';
}

export default function StatusBadge({
    status: rawStatus,
    className,
    isDraft = false,
    draftLabel,
}: StatusBadgeProps) {
    const { t } = useTranslation();
    const status = toBadgeStatus(rawStatus);

    const label = t(`batchServiceStatus.${status}`);
    const capitalizedLabel = label.charAt(0).toUpperCase() + label.slice(1);

    const badge = (
        <Badge
            tone={TONE[status]}
            size="md"
            icon={
                <SvgIcon
                    icon={STATUS_ICON[status]}
                    size="custom"
                    customSize={12}
                    spin={status === 'created' || status === 'started'}
                    svgProps={{ 'aria-hidden': true }}
                />
            }>
            {capitalizedLabel}
        </Badge>
    );

    if (isDraft && draftLabel) {
        return (
            <BadgeGroup className={className}>
                {badge}
                <Badge size="md">{draftLabel}</Badge>
            </BadgeGroup>
        );
    }

    return <div className={className}>{badge}</div>;
}
